// Curated starter dictionary (Milestone 2). Intentionally small and discovery-
// friendly; later Editions expand it (see IMPLEMENTATION_PLAN Milestone 4/8).
// A flat Set gives O(1) validation for this size; swap in a trie when the list
// grows large. All words are uppercase, the canonical in-game form.

export interface WordSet {
  id: string
  name: string
  members: string[]
}

// Thematic collections — completing one grants a capped global bonus (§8.8).
export const WORD_SETS: WordSet[] = [
  { id: 'animals', name: 'Animals', members: ['CAT', 'DOG', 'FOX', 'OWL', 'BAT', 'FROG', 'BEAR', 'LION', 'WOLF', 'HAWK'] },
  { id: 'colors', name: 'Colors', members: ['RED', 'BLUE', 'CYAN', 'GRAY', 'PINK', 'TAN', 'ROSE', 'SAGE', 'MAUVE', 'IVORY'] },
  { id: 'elements', name: 'Elements', members: ['FIRE', 'WATER', 'STONE', 'AIR', 'IRON', 'ASH', 'RUST', 'COAL', 'SAND', 'WAX'] },
]

// General vocabulary available from early letters, plus the set members above.
const GENERAL: string[] = [
  'THE', 'AND', 'INK', 'PEN', 'TEA', 'SEA', 'SUN', 'SKY', 'ICE', 'ART',
  'RED', 'BIG', 'OLD', 'NEW', 'HOT', 'COLD', 'TALL', 'FAIR', 'WISE', 'BOLD',
  'NICE', 'KIND', 'SLOW', 'FAST', 'LOUD', 'CALM', 'PURE', 'WILD', 'KEEN', 'DEAR',
  'HEART', 'SOUL', 'MIND', 'BRAIN', 'BLOOD', 'BONES', 'LIGHT', 'DARK', 'COLD', 'WARM',
  'RAIN', 'TREE', 'ROOT', 'LEAF', 'WORD', 'NOTE', 'TONE', 'LINE', 'TIME',
  'STAR', 'MOON', 'WIND', 'SONG', 'HERO', 'TALE', 'MYTH', 'EPIC', 'MOOD', 'MODE',
  'BOOK', 'POEM', 'RHYME', 'VERSE', 'PAGES', 'STORY', 'TITLE', 'THEME',
  'WALK', 'RUN', 'JUMP', 'SWIM', 'FLY', 'ROW', 'SING', 'READ', 'WRITE', 'DRAW',
  'COOK', 'BAKE', 'BREW', 'POUR', 'MELT', 'GLOW', 'SHINE', 'PULSE', 'SPIN', 'FALL',
  'RISE', 'WAKE', 'SLEEP', 'DREAM', 'HOPE', 'FEAR', 'LOVE', 'HATE', 'FEEL', 'THINK',
  'TALK', 'SAY', 'TELL', 'ASK', 'GIVE', 'TAKE', 'MAKE', 'DO', 'USE', 'TRY',
  'I', 'A', 'O', 'AN', 'IN', 'ON', 'AT', 'TO', 'OF', 'OR',
  'AM', 'IS', 'BE', 'NO', 'SO', 'IF', 'BY', 'MY', 'US', 'WE',
  'HE', 'SHE', 'IT', 'ME', 'HIM', 'HER', 'HIS', 'OUR', 'ITS', 'ALL',
  'ANY', 'EACH', 'EVERY', 'MUCH', 'SOME', 'MOST', 'MORE', 'LESS', 'FEW', 'MANY',
  'FOREST', 'GARDEN', 'SILENT', 'LISTEN', 'WONDER', 'LETTER', 'WRITER',
  'QUARTZ', 'ZEPHYR', 'EQUATION', 'JINX', 'PRIZE', 'VOWEL',
]

// Every craftable word = general list ∪ all set members (deduped).
export const WORDS: string[] = Array.from(
  new Set([...GENERAL, ...WORD_SETS.flatMap((s) => s.members)]),
).sort()

const WORD_LOOKUP = new Set(WORDS)
export function isValidWord(word: string): boolean {
  return WORD_LOOKUP.has(word.toUpperCase())
}

// word -> set id (a word may belong to at most one set for bonus purposes).
export const SET_BY_WORD: Record<string, string> = (() => {
  const m: Record<string, string> = {}
  for (const s of WORD_SETS) for (const w of s.members) if (!(w in m)) m[w] = s.id
  return m
})()

// Deterministic Daily Word: stable per calendar day, drawn from the full list.
export function dailyKey(date = new Date()): string {
  return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`
}

export function dailyWord(date = new Date()): string {
  const key = dailyKey(date)
  let h = 0
  for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) | 0
  return WORDS[Math.abs(h) % WORDS.length]
}
